/**
 * A searched place that is a street address ("1739 Grand Pne Pt"), kept so
 * the map can open that home once the search results arrive.
 */
export interface AddressSearchTarget {
  house: string;
  lat: number;
  lng: number;
  at: number;
}

interface SearchedPlaceLike {
  name?: string;
  geometry?: unknown;
}

interface PlaceGeometry {
  location?: { lat: () => number; lng: () => number };
}

/** How long a searched address may wait for its results to load. */
export const ADDRESS_SEARCH_TTL_MS = 15000;

/**
 * Google and the MLS spell streets differently ("Grand Pne Pt" against
 * "Grand Pine Point"), so a home matches on its house number and lying within
 * this distance of the searched point.
 */
const MATCH_RADIUS_METERS = 300;

const HOUSE_NUMBER = /^(\d+[a-z]?)\s+\S/i;

const houseNumberOf = (address: string): string | undefined =>
  HOUSE_NUMBER.exec(address.trim())?.[1]?.toLowerCase();

const metersBetween = (
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};

/** Returns a target only when the searched place starts with a house number. */
export function addressSearchTarget(
  place: SearchedPlaceLike | undefined,
  now: number = Date.now()
): AddressSearchTarget | null {
  const house = houseNumberOf(place?.name ?? '');
  const location = (place?.geometry as PlaceGeometry | undefined)?.location;
  if (!house || !location) return null;
  return { house, lat: location.lat(), lng: location.lng(), at: now };
}

/**
 * The one result at the searched address, or undefined when none or several
 * match. Handles both MLS rows (flat address, lat/lng) and assumable rows
 * (nested address, latitude/longitude).
 */
export function findAddressMatch<T>(
  properties: T[],
  target: AddressSearchTarget
): T | undefined {
  const matches = properties.filter((property) => {
    const p = property as any;
    const address =
      typeof p?.address === 'string' ? p.address : p?.address?.address;
    if (!address || houseNumberOf(address) !== target.house) return false;
    const lat = Number(p.lat ?? p.latitude);
    const lng = Number(p.lng ?? p.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    return (
      metersBetween(lat, lng, target.lat, target.lng) <= MATCH_RADIUS_METERS
    );
  });
  return matches.length === 1 ? matches[0] : undefined;
}

const normalizeTyped = (value: string): string =>
  value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * The one suggestion a buyer has finished typing out by hand: their text runs
 * through the street, city and state of exactly one suggestion and does not
 * disagree with the rest of it ("1739 Grand Pine Point Maryville TN" against
 * "1739 Grand Pine Point, Maryville, TN, 37801"). Only the ZIP may be left
 * off; a buyer who stops sooner may still be typing, so nothing is picked.
 */
export function typedAddressMatch<
  T extends { title?: string; isNew?: boolean }
>(typed: string, options: T[]): T | undefined {
  const input = normalizeTyped(typed);
  if (!/^\d/.test(input)) return undefined;
  const matches = options.filter((option) => {
    if (option.isNew || !option.title) return false;
    const [street, city, stateAndZip] = option.title.split(',');
    const state = stateAndZip?.trim().split(/\s+/)[0];
    if (!street || !city || !state) return false;
    const full = normalizeTyped(option.title);
    const throughState = normalizeTyped(`${street} ${city} ${state}`);
    return input.startsWith(throughState) && full.startsWith(input);
  });
  return matches.length === 1 ? matches[0] : undefined;
}
